/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { assistantsSetExecutionProfileChoice } from "../funcs/assistantsSetExecutionProfileChoice.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AssistantExecutionProfile } from "../models/components/assistantexecutionprofile.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetAssistantExecutionProfileChoiceRequest,
  SetAssistantExecutionProfileChoiceSecurity,
} from "../models/operations/setassistantexecutionprofilechoice.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetAssistantExecutionProfileChoiceMutationVariables = {
  request: SetAssistantExecutionProfileChoiceRequest;
  security?: SetAssistantExecutionProfileChoiceSecurity | undefined;
  options?: RequestOptions;
};

export type SetAssistantExecutionProfileChoiceMutationData =
  AssistantExecutionProfile;

export type SetAssistantExecutionProfileChoiceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setExecutionProfileChoice assistants
 *
 * @remarks
 * Choose the upstream credential an assistant's turns use on one MCP server, for one root trigger and execution context: an auth client, no upstream authentication, or back to the choice the next turn makes on its own. Turns already running keep their choice; later turns use the new one. Turns that act for nobody can use shared credentials only. Choosing grants nothing: each request checks the turn may use the credential. Requires assistant write access.
 */
export function useSetAssistantExecutionProfileChoiceMutation(
  options?: MutationHookOptions<
    SetAssistantExecutionProfileChoiceMutationData,
    SetAssistantExecutionProfileChoiceMutationError,
    SetAssistantExecutionProfileChoiceMutationVariables
  >,
): UseMutationResult<
  SetAssistantExecutionProfileChoiceMutationData,
  SetAssistantExecutionProfileChoiceMutationError,
  SetAssistantExecutionProfileChoiceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetAssistantExecutionProfileChoiceMutation(client, options),
    ...options,
  });
}

export function mutationKeySetAssistantExecutionProfileChoice(): MutationKey {
  return ["@gram/client", "assistants", "setExecutionProfileChoice"];
}

export function buildSetAssistantExecutionProfileChoiceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetAssistantExecutionProfileChoiceMutationVariables,
  ) => Promise<SetAssistantExecutionProfileChoiceMutationData>;
} {
  return {
    mutationKey: mutationKeySetAssistantExecutionProfileChoice(),
    mutationFn: function setAssistantExecutionProfileChoiceMutationFn({
      request,
      security,
      options,
    }): Promise<SetAssistantExecutionProfileChoiceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(assistantsSetExecutionProfileChoice(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
